import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Phone, Mail } from "lucide-react";
import { adminList, adminUpdate, adminDelete } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { waLink } from "@/lib/whatsapp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS = ["new", "contacted", "closed"];
const statusColor = { new: "bg-primary/15 text-primary", contacted: "bg-amber-100 text-amber-700", closed: "bg-green-100 text-green-700" };

const Enquiries = () => {
  const qc = useQueryClient();
  const site = useSite();
  const { data: items = [], isLoading } = useQuery({ queryKey: ["enquiries"], queryFn: () => adminList("enquiries") });

  const setStatus = async (id, status) => {
    try {
      await adminUpdate("enquiries", id, { status });
      qc.invalidateQueries({ queryKey: ["enquiries"] });
      toast.success("Status updated");
    } catch { toast.error("Could not update status. Please retry."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await adminDelete("enquiries", id);
      qc.invalidateQueries({ queryKey: ["enquiries"] });
      toast.success("Deleted");
    } catch { toast.error("Could not delete. Please retry."); }
  };

  return (
    <div data-testid="admin-enquiries">
      <h1 className="font-heading text-4xl text-secondary">Enquiries</h1>
      <p className="text-muted-foreground mt-1">All leads from forms and WhatsApp.</p>

      {isLoading ? <p className="mt-8 text-muted-foreground">Loading…</p> : items.length === 0 ? (
        <p className="mt-8 text-muted-foreground" data-testid="no-enquiries">No enquiries yet.</p>
      ) : (
        <div className="mt-8 space-y-4">
          {items.map((e) => (
            <div key={e.id} className="bg-white border border-border rounded-xl p-5" data-testid={`enquiry-row-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading text-2xl text-secondary">{e.name}</h3>
                    <span className={`text-xs rounded-full px-2.5 py-0.5 font-semibold capitalize ${statusColor[e.status] || ""}`}>{e.status}</span>
                    {e.source === "whatsapp" && <span className="text-xs rounded-full px-2.5 py-0.5 bg-whatsapp/15 text-whatsapp font-semibold">WhatsApp</span>}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {e.pooja_interested && <span className="font-medium text-secondary">{e.pooja_interested}</span>}
                    {e.problem && ` · ${e.problem}`}
                    {e.city && ` · ${e.city}`}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString("en-IN")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={e.status} onValueChange={(v) => setStatus(e.id, v)}>
                    <SelectTrigger className="w-36 h-9" data-testid={`status-select-${e.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button onClick={() => remove(e.id)} data-testid={`delete-enquiry-${e.id}`} className="p-2 rounded-md border border-border text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1.5 text-secondary hover:text-primary"><Phone className="h-4 w-4" /> {e.phone}</a>
                {(e.whatsapp || e.phone) && <a href={waLink(e.whatsapp || e.phone, `Namaste ${e.name} 🙏`)} target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-1.5 text-whatsapp hover:opacity-80"><Phone className="h-4 w-4" /> WhatsApp</a>}
                {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 text-secondary hover:text-primary"><Mail className="h-4 w-4" /> {e.email}</a>}
                {e.preferred_date && <span className="text-muted-foreground">Preferred: {e.preferred_date}</span>}
              </div>
              {e.notes && <p className="mt-3 text-sm text-muted-foreground bg-muted/50 rounded-md p-3">{e.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Enquiries;
